import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail, ShieldCheck, Users } from "lucide-react";

export const CREST_URL = "/godsc-crest.png";

export const CONSTITUTION_URL =
  "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/mmidl4lg_GODSC_Constitution_2017.pdf";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/leadership", label: "Leadership" },
  { to: "/members", label: "Line History" },
  { to: "/events", label: "Events" },
  { to: "/contact", label: "Contact" },
];

const SiteLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      {/* Utility Bar */}
      <div className="bg-[#D4AF37] text-black text-[11px] tracking-[0.22em] uppercase">
        <div className="max-w-7xl mx-auto px-5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4" data-testid="utility-meta">
            <span className="hidden sm:inline">Est. 1982 &middot; Howard University</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://instagram.com/godscinc"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 hover:text-[#0A1F44] transition-colors"
              data-testid="utility-instagram"
            >
              <Instagram size={13} /> <span className="hidden sm:inline">@godscinc</span>
            </a>
            <Link
              to="/contact"
              className="flex items-center gap-1.5 hover:text-[#0A1F44] transition-colors"
              data-testid="utility-contact"
            >
              <Mail size={13} /> <span className="hidden sm:inline">Contact</span>
            </Link>
            <Link
              to="/member-login"
              className="flex items-center gap-1.5 hover:text-[#0A1F44] transition-colors"
              data-testid="utility-member-portal"
            >
              <Users size={13} /> <span className="hidden sm:inline">Members</span>
            </Link>
            <Link
              to="/admin/login"
              className="flex items-center gap-1.5 hover:text-[#0A1F44] transition-colors"
              data-testid="utility-admin-login"
            >
              <ShieldCheck size={13} /> <span className="hidden sm:inline">Admin</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header className="bg-[#FDFBF7] border-b border-[#D8D3C4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={CREST_URL} alt="GODSC Crest" className="h-14 w-14 object-contain" />
            <div className="leading-tight">
              <div className="font-serif-display text-[22px] font-semibold text-[#0A1F44]">
                GODSC
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#5C5C5C]">
                Gentlemen of Drew Social Club Inc.
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary-navy" data-testid="nav-cta">
              Interest Form
            </Link>
          </nav>

          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#0A1F44]"
            data-testid="mobile-nav-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile nav */}
        {open && (
          <div className="lg:hidden border-t border-[#D8D3C4] bg-[#FDFBF7]" data-testid="mobile-nav">
            <div className="px-5 py-4 flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={`nav-link ${pathname === l.to ? "active" : ""}`}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-primary-navy text-center mt-2"
                data-testid="mobile-nav-cta"
              >
                Interest Form
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#061430] text-[#EBE8E0] mt-10" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-5 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={CREST_URL} alt="GODSC" className="h-12 w-12 object-contain" />
              <div>
                <div className="font-serif-display text-2xl text-white">GODSC</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                  Gentlemen of Drew Social Club Inc. &middot; Est. 1982
                </div>
              </div>
            </div>
            <p className="text-sm text-[#C9C9C9] leading-relaxed max-w-md">
              The Gentlemen of Drew Social Club Incorporated &mdash; a selective freshman
              brotherhood of Howard University, founded on the principles of academic
              excellence, professional development, and service.
            </p>
          </div>
          <div>
            <div className="overline text-[#D4AF37] mb-4">Explore</div>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.slice(0, 6).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-[#D4AF37] transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href={CONSTITUTION_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#D4AF37] transition-colors"
                  data-testid="footer-constitution"
                >
                  Constitution (2017)
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="overline text-[#D4AF37] mb-4">Connect</div>
            <ul className="space-y-2 text-sm">
              <li>Howard University</li>
              <li>Washington, D.C. 20059</li>
              <li>
                <a href="mailto:Godscinc1982@gmail.com" className="hover:text-[#D4AF37]">
                  Godscinc1982@gmail.com
                </a>
              </li>
              <li>
                <Link to="/contact" className="hover:text-[#D4AF37]">
                  Prospective Member Form
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#1b2a4e]">
          <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs tracking-[0.18em] uppercase text-[#8994a8]">
            <span>&copy; {new Date().getFullYear()} GODSC, Inc. All Rights Reserved.</span>
            <span>Success Is</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SiteLayout;
